import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { useToast } from "../context/ToastContext";
import {
  buildSampleIssuePoPdfDocument,
  buildSamplePoPreviewHtml,
} from "../utils/constructionPoDocument";
import { downloadPdfFromHtml, warmPoPdfLogoCache } from "../utils/downloadPdfFromHtml";
import {
  defaultPoFormatSettings,
  gstEditorRowsToSaved,
  loadPoFormatSettings,
  normalizePoFormatSettings,
  savePoFormatSettings,
  type PoFormatSettings,
} from "../utils/poFormatSettings";

type GstRow = { id: string; label: string; rateInput: string };

function rowsFromSettings(s: PoFormatSettings): GstRow[] {
  return s.gstRows.map((r, i) => ({
    id: `gst-${i}-${r.label.replace(/\W+/g, "").slice(0, 8)}`,
    label: r.label,
    rateInput: String(r.ratePercent),
  }));
}

export function PoFormatSettingsPage() {
  const { showToast } = useToast();
  const initial = useMemo(() => loadPoFormatSettings(), []);

  const [documentTitle, setDocumentTitle] = useState(initial.documentTitle);
  const [letterheadTagline, setLetterheadTagline] = useState(initial.letterheadTagline);
  const [logoPrimaryUrl, setLogoPrimaryUrl] = useState(initial.logoPrimaryUrl);
  const [logoSecondaryUrl, setLogoSecondaryUrl] = useState(initial.logoSecondaryUrl);
  const [logoPrimaryHeightPx, setLogoPrimaryHeightPx] = useState(initial.logoPrimaryHeightPx);
  const [logoSecondaryHeightPx, setLogoSecondaryHeightPx] = useState(initial.logoSecondaryHeightPx);
  const [accentColorHex, setAccentColorHex] = useState(initial.accentColorHex);
  const [footerNote, setFooterNote] = useState(initial.footerNote);
  const [buyerName, setBuyerName] = useState(initial.buyerName);
  const [buyerAddress, setBuyerAddress] = useState(initial.buyerAddress);
  const [buyerGstin, setBuyerGstin] = useState(initial.buyerGstin);
  const [defaultVendorName, setDefaultVendorName] = useState(initial.defaultVendorName);
  const [defaultVendorAddress, setDefaultVendorAddress] = useState(initial.defaultVendorAddress);
  const [defaultVendorGstin, setDefaultVendorGstin] = useState(initial.defaultVendorGstin);
  const [paymentTerms, setPaymentTerms] = useState(initial.paymentTerms);
  const [defaultShipTo, setDefaultShipTo] = useState(initial.defaultShipTo);
  const [aggregateQtyDefault, setAggregateQtyDefault] = useState(initial.aggregateQtyDefault);
  const [gstRows, setGstRows] = useState<GstRow[]>(() => rowsFromSettings(initial));
  const [samplePdfBusy, setSamplePdfBusy] = useState(false);
  const [activeTab, setActiveTab] = useState<"format" | "preview">("format");

  function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>, setter: (v: string) => void) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (typeof ev.target?.result === "string") {
        setter(ev.target.result);
      }
    };
    reader.readAsDataURL(file);
  }

  function addGstRow() {
    setGstRows((rows) => [...rows, { id: `gst-${Date.now()}`, label: "IGST", rateInput: "18" }]);
  }

  function removeGstRow(id: string) {
    setGstRows((rows) => rows.filter((r) => r.id !== id));
  }

  function handleSave() {
    const draft: PoFormatSettings = normalizePoFormatSettings({
      version: 1,
      documentTitle: documentTitle.trim(),
      letterheadTagline: letterheadTagline.trim(),
      logoPrimaryUrl: logoPrimaryUrl.trim(),
      logoSecondaryUrl: logoSecondaryUrl.trim(),
      logoPrimaryHeightPx,
      logoSecondaryHeightPx,
      accentColorHex: accentColorHex.trim(),
      footerNote: footerNote.trim(),
      buyerName: buyerName.trim(),
      buyerAddress: buyerAddress.trim(),
      buyerGstin: buyerGstin.trim(),
      defaultVendorName: defaultVendorName.trim(),
      defaultVendorAddress: defaultVendorAddress.trim(),
      defaultVendorGstin: defaultVendorGstin.trim(),
      paymentTerms: paymentTerms.trim(),
      defaultShipTo: defaultShipTo.trim(),
      aggregateQtyDefault,
      gstRows: gstEditorRowsToSaved(gstRows),
    });
    savePoFormatSettings(draft);
    showToast("PO format saved. Downloads will use these defaults.", "success");
  }

  function handleResetDefaults() {
    const d = defaultPoFormatSettings();
    setDocumentTitle(d.documentTitle);
    setLetterheadTagline(d.letterheadTagline);
    setLogoPrimaryUrl(d.logoPrimaryUrl);
    setLogoSecondaryUrl(d.logoSecondaryUrl);
    setLogoPrimaryHeightPx(d.logoPrimaryHeightPx);
    setLogoSecondaryHeightPx(d.logoSecondaryHeightPx);
    setAccentColorHex(d.accentColorHex);
    setFooterNote(d.footerNote);
    setBuyerName(d.buyerName);
    setBuyerAddress(d.buyerAddress);
    setBuyerGstin(d.buyerGstin);
    setDefaultVendorName(d.defaultVendorName);
    setDefaultVendorAddress(d.defaultVendorAddress);
    setDefaultVendorGstin(d.defaultVendorGstin);
    setPaymentTerms(d.paymentTerms);
    setDefaultShipTo(d.defaultShipTo);
    setAggregateQtyDefault(d.aggregateQtyDefault);
    setGstRows(rowsFromSettings(d));
    showToast("Form reset to factory defaults (not saved yet).", "info");
  }

  const samplePreviewInput = useMemo(
    () => ({
      documentTitle: documentTitle.trim(),
      letterheadTagline: letterheadTagline.trim(),
      logoPrimaryUrl: logoPrimaryUrl.trim(),
      logoSecondaryUrl: logoSecondaryUrl.trim(),
      logoPrimaryHeightPx,
      logoSecondaryHeightPx,
      accentColorHex: accentColorHex.trim(),
      footerNote: footerNote.trim(),
      buyerName: buyerName.trim(),
      buyerAddress: buyerAddress.trim(),
      buyerGstin: buyerGstin.trim(),
      defaultVendorName: defaultVendorName.trim(),
      defaultVendorAddress: defaultVendorAddress.trim(),
      defaultVendorGstin: defaultVendorGstin.trim(),
      paymentTerms: paymentTerms.trim(),
      defaultShipTo: defaultShipTo.trim(),
      gstRows: gstEditorRowsToSaved(gstRows),
    }),
    [
      documentTitle,
      letterheadTagline,
      logoPrimaryUrl,
      logoSecondaryUrl,
      logoPrimaryHeightPx,
      logoSecondaryHeightPx,
      accentColorHex,
      footerNote,
      buyerName,
      buyerAddress,
      buyerGstin,
      defaultVendorName,
      defaultVendorAddress,
      defaultVendorGstin,
      paymentTerms,
      defaultShipTo,
      gstRows,
    ],
  );

  const previewHtml = useMemo(() => buildSamplePoPreviewHtml(samplePreviewInput), [samplePreviewInput]);

  const samplePdfDoc = useMemo(
    () => buildSampleIssuePoPdfDocument(samplePreviewInput),
    [samplePreviewInput],
  );

  useEffect(() => {
    void warmPoPdfLogoCache([logoPrimaryUrl, logoSecondaryUrl]);
  }, [logoPrimaryUrl, logoSecondaryUrl]);

  return (
    <div className="workspace" style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 4rem)", background: "#f1f5f9" }}>
      <div style={{ width: "100%", padding: "1rem", flex: "0 0 auto", display: "flex", justifyContent: "center" }}>
        <div style={{ display: "flex", gap: "0.25rem", background: "#cbd5e1", padding: "0.3rem", borderRadius: "8px" }}>
          <button 
            type="button" 
            className={`btn ${activeTab === "format" ? "btn--primary" : ""}`}
            style={activeTab === "format" ? { boxShadow: "0 1px 3px rgba(0,0,0,0.1)", minWidth: "140px" } : { background: "transparent", border: "none", color: "#334155", minWidth: "140px", fontWeight: 500 }}
            onClick={() => setActiveTab("format")}
          >
            Format Settings
          </button>
          <button 
            type="button" 
            className={`btn ${activeTab === "preview" ? "btn--primary" : ""}`}
            style={activeTab === "preview" ? { boxShadow: "0 1px 3px rgba(0,0,0,0.1)", minWidth: "140px" } : { background: "transparent", border: "none", color: "#334155", minWidth: "140px", fontWeight: 500 }}
            onClick={() => setActiveTab("preview")}
          >
            Live Preview
          </button>
        </div>
      </div>

      <div style={{ display: "flex", flex: 1, overflow: "hidden", gap: "1rem", padding: "0 1rem 1rem 1rem", justifyContent: "center" }}>
        
        {/* LEFT PANE - Settings */}
        {activeTab === "format" && (
        <div className="card card--gov card--elevated" style={{ flex: 1, maxWidth: "1200px", overflowY: "auto", margin: 0, padding: 0, display: "flex", flexDirection: "column" }}>
          
          <div style={{ padding: "1.5rem" }}>
            <p className="section-lead section-lead--tight text-muted" style={{ marginTop: 0 }}>
              Use the vendor block below for the supplier name, address, and GSTIN shown on every PO and PDF. If vendor
              name is left blank, the subcontractor firm on that PO is used as the supplier name.
            </p>

            <div className="inv-outward-issue__grid">
              <label className="inv-outward-issue__field inv-outward-issue__field--full">
                <span className="inv-outward-issue__label">Document title</span>
                <input
                  className="form-control"
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                  placeholder="PURCHASE ORDER"
                />
              </label>
              <label className="inv-outward-issue__field inv-outward-issue__field--full">
                <span className="inv-outward-issue__label">Letterhead tagline (small caps line above title)</span>
                <input
                  className="form-control"
                  value={letterheadTagline}
                  onChange={(e) => setLetterheadTagline(e.target.value)}
                  placeholder="e.g. Programme / department name"
                />
              </label>
              <label className="inv-outward-issue__field inv-outward-issue__field--r1">
                <span className="inv-outward-issue__label">Accent colour (#RRGGBB)</span>
                <input
                  type="color"
                  className="form-control"
                  style={{ maxWidth: "72px", height: "38px", padding: "4px" }}
                  value={/^#[0-9A-Fa-f]{6}$/.test(accentColorHex.trim()) ? accentColorHex.trim() : "#16365f"}
                  onChange={(e) => setAccentColorHex(e.target.value)}
                  aria-label="Accent colour"
                />
              </label>
              <label className="inv-outward-issue__field inv-outward-issue__field--r1">
                <span className="inv-outward-issue__label">Accent hex (manual)</span>
                <input
                  className="form-control mono"
                  value={accentColorHex}
                  onChange={(e) => setAccentColorHex(e.target.value)}
                  placeholder="#16365f"
                />
              </label>
              <div className="inv-outward-issue__field inv-outward-issue__field--full" style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: "1rem" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <label className="inv-outward-issue__field">
                    <span className="inv-outward-issue__label">Primary logo (upload image or provide URL)</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="form-control"
                      style={{ padding: "0.4rem 0.6rem" }}
                      onChange={(e) => handleLogoUpload(e, setLogoPrimaryUrl)}
                    />
                  </label>
                  <input
                    className="form-control mono"
                    value={logoPrimaryUrl}
                    onChange={(e) => setLogoPrimaryUrl(e.target.value)}
                    placeholder="Optional — leave blank for no logo, or paste an https:// URL / base64 data URL"
                  />
                </div>
                {logoPrimaryUrl && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", border: "1px dashed var(--border)", borderRadius: "8px", padding: "0.5rem", background: "#f8fafc" }}>
                    <img src={logoPrimaryUrl} alt="Primary preview" style={{ maxHeight: "80px", maxWidth: "100%", objectFit: "contain" }} />
                  </div>
                )}
              </div>
              <label className="inv-outward-issue__field inv-outward-issue__field--r1">
                <span className="inv-outward-issue__label">Primary logo height (px)</span>
                <input
                  type="number"
                  min={28}
                  max={160}
                  className="form-control mono"
                  value={Number.isFinite(logoPrimaryHeightPx) ? logoPrimaryHeightPx : ""}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setLogoPrimaryHeightPx(Number.isFinite(v) ? v : defaultPoFormatSettings().logoPrimaryHeightPx);
                  }}
                />
              </label>
              <div className="inv-outward-issue__field inv-outward-issue__field--full" style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: "1rem" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <label className="inv-outward-issue__field">
                    <span className="inv-outward-issue__label">Secondary logo (optional upload or URL)</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="form-control"
                      style={{ padding: "0.4rem 0.6rem" }}
                      onChange={(e) => handleLogoUpload(e, setLogoSecondaryUrl)}
                    />
                  </label>
                  <input
                    className="form-control mono"
                    value={logoSecondaryUrl}
                    onChange={(e) => setLogoSecondaryUrl(e.target.value)}
                    placeholder="/branding/your-company.png or base64..."
                  />
                </div>
                {logoSecondaryUrl && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", border: "1px dashed var(--border)", borderRadius: "8px", padding: "0.5rem", background: "#f8fafc" }}>
                    <img src={logoSecondaryUrl} alt="Secondary preview" style={{ maxHeight: "80px", maxWidth: "100%", objectFit: "contain" }} />
                  </div>
                )}
              </div>
              <label className="inv-outward-issue__field inv-outward-issue__field--r1">
                <span className="inv-outward-issue__label">Secondary logo height (px)</span>
                <input
                  type="number"
                  min={28}
                  max={160}
                  className="form-control mono"
                  value={Number.isFinite(logoSecondaryHeightPx) ? logoSecondaryHeightPx : ""}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setLogoSecondaryHeightPx(Number.isFinite(v) ? v : defaultPoFormatSettings().logoSecondaryHeightPx);
                  }}
                />
              </label>
              <p className="text-muted inv-outward-issue__field inv-outward-issue__field--full" style={{ fontSize: "0.86rem", margin: "-0.25rem 0 0.35rem" }}>
                You can either upload an image from your computer directly, or paste an existing URL.
              </p>
              <label className="inv-outward-issue__field inv-outward-issue__field--full">
                <span className="inv-outward-issue__label">Footer note (printed under totals)</span>
                <textarea
                  className="form-control"
                  rows={2}
                  value={footerNote}
                  onChange={(e) => setFooterNote(e.target.value)}
                  placeholder="Optional — e.g. statutory declaration or office address repeat"
                />
              </label>
              <label className="inv-outward-issue__field inv-outward-issue__field--full">
                <span className="inv-outward-issue__label">Purchaser / company name</span>
                <input className="form-control" value={buyerName} onChange={(e) => setBuyerName(e.target.value)} />
              </label>
              <label className="inv-outward-issue__field inv-outward-issue__field--full">
                <span className="inv-outward-issue__label">Purchaser address</span>
                <textarea className="form-control" rows={3} value={buyerAddress} onChange={(e) => setBuyerAddress(e.target.value)} />
              </label>
              <label className="inv-outward-issue__field inv-outward-issue__field--r1">
                <span className="inv-outward-issue__label">Purchaser GSTIN</span>
                <input
                  className="form-control mono"
                  value={buyerGstin}
                  onChange={(e) => setBuyerGstin(e.target.value.toUpperCase())}
                />
              </label>
              <label className="inv-outward-issue__field inv-outward-issue__field--full">
                <span className="inv-outward-issue__label">Default vendor / supplier name</span>
                <input
                  className="form-control"
                  value={defaultVendorName}
                  onChange={(e) => setDefaultVendorName(e.target.value)}
                  placeholder="Leave blank to use the PO subcontractor firm name"
                />
              </label>
              <label className="inv-outward-issue__field inv-outward-issue__field--full">
                <span className="inv-outward-issue__label">Default vendor address (prefill)</span>
                <textarea
                  className="form-control"
                  rows={2}
                  value={defaultVendorAddress}
                  onChange={(e) => setDefaultVendorAddress(e.target.value)}
                />
              </label>
              <label className="inv-outward-issue__field inv-outward-issue__field--r1">
                <span className="inv-outward-issue__label">Default vendor GSTIN (prefill)</span>
                <input
                  className="form-control mono"
                  value={defaultVendorGstin}
                  onChange={(e) => setDefaultVendorGstin(e.target.value.toUpperCase())}
                />
              </label>
              <label className="inv-outward-issue__field inv-outward-issue__field--full">
                <span className="inv-outward-issue__label">Default ship to / site text</span>
                <textarea className="form-control" rows={2} value={defaultShipTo} onChange={(e) => setDefaultShipTo(e.target.value)} />
              </label>
              <label className="inv-outward-issue__field inv-outward-issue__field--full">
                <span className="inv-outward-issue__label">Payment &amp; terms</span>
                <textarea className="form-control" rows={3} value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} />
              </label>
              <div className="inv-outward-issue__field inv-outward-issue__field--full">
                <span className="inv-outward-issue__label">Download panel default</span>
                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 400, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={aggregateQtyDefault}
                    onChange={(e) => setAggregateQtyDefault(e.target.checked)}
                  />
                  <span>Start with aggregated quantities per item when opening PO download</span>
                </label>
              </div>
            </div>
          </div>
          
          <div style={{ padding: "0 1.5rem 1.5rem" }}>
            <h3 className="form-section-title">Default GST rows</h3>
            <p className="text-muted" style={{ fontSize: "0.88rem", marginTop: "-0.35rem" }}>
              Applied on each PO download; tax is computed on taxable value (qty × rate). You can still change rows before
              downloading.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "0.75rem" }}>
              {gstRows.map((row) => (
                <div key={row.id} style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}>
                  <input
                    className="form-control"
                    style={{ width: "120px" }}
                    value={row.label}
                    onChange={(e) =>
                      setGstRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, label: e.target.value } : r)))
                    }
                    aria-label="GST label"
                  />
                  <input
                    className="form-control mono"
                    style={{ width: "88px" }}
                    value={row.rateInput}
                    onChange={(e) =>
                      setGstRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, rateInput: e.target.value } : r)))
                    }
                    aria-label="GST percent"
                  />
                  <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                    %
                  </span>
                  <button type="button" className="btn btn--secondary btn--sm" onClick={() => removeGstRow(row.id)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <div className="toolbar-row" style={{ marginBottom: "1rem" }}>
              <button type="button" className="btn btn--tg-secondary btn--sm" onClick={addGstRow}>
                Add GST row
              </button>
            </div>
          </div>
          
          {/* Sticky Footer */}
          <div style={{ position: "sticky", bottom: 0, background: "#fff", padding: "1rem 1.5rem", borderTop: "1px solid var(--border)", marginTop: "auto", zIndex: 10 }}>
            <div className="toolbar-row">
              <button type="button" className="btn btn--primary btn--tg btn--lg" onClick={handleSave}>
                Save PO format
              </button>
              <button type="button" className="btn btn--tg-secondary btn--lg" onClick={handleResetDefaults}>
                Reset
              </button>
              <Link className="btn btn--tg-secondary btn--lg" to="/inventory">
                Back
              </Link>
            </div>
          </div>
        </div>
        )}

        {/* RIGHT PANE - Live Preview */}
        {activeTab === "preview" && (
        <div className="card card--gov card--elevated" style={{ flex: 1, maxWidth: "1200px", margin: 0, padding: 0, overflowX: "auto", overflowY: "hidden", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "1rem", borderBottom: "1px solid var(--border)", background: "#f8fafc", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h3 style={{ margin: 0, fontSize: "1rem", color: "var(--text)" }}>Live PDF Preview</h3>
              <p className="text-muted" style={{ margin: "0.2rem 0 0", fontSize: "0.85rem" }}>Updates instantly. Uses sample materials.</p>
            </div>
            <button
              type="button"
              className="btn btn--primary btn--tg btn--sm"
              disabled={samplePdfBusy}
              onClick={() => {
                void (async () => {
                  setSamplePdfBusy(true);
                  try {
                    await downloadPdfFromHtml(
                      buildSamplePoPreviewHtml(samplePreviewInput, { forPdfCapture: true }),
                      "PO_SAMPLE_PREVIEW",
                      {
                        annexure: {
                          allocations: samplePdfDoc.allocations,
                          accentColorHex: samplePdfDoc.accentColorHex,
                          footerNote: samplePdfDoc.footerNote,
                        },
                      },
                    );
                    showToast("Sample PO PDF downloaded.", "success");
                  } catch (e) {
                    showToast(e instanceof Error ? e.message : "Could not create PDF.", "error");
                  } finally {
                    setSamplePdfBusy(false);
                  }
                })();
              }}
            >
              {samplePdfBusy ? "Creating PDF…" : "Download PDF"}
            </button>
          </div>
          <iframe 
            srcDoc={previewHtml} 
            style={{ flex: 1, width: "100%", minWidth: "800px", border: "none", background: "#cbd5e1" }} 
            title="Live PO Preview"
          />
        </div>
        )}
      </div>
    </div>
  );
}
